'use client';

import { ResumeIntakeDialog } from './ResumeIntakeDialog';
import type { IntakeHandlerResult, IntakeProgressReporter } from './ResumeIntakePanel';

interface ResumeUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded?: () => void | Promise<void>;
}

/**
 * Thin wrapper around the unified ResumeIntakeDialog. Used by surfaces that
 * just want an upload path (no list, no create flow surfaced first). The
 * underlying panel still shows the action tiles so users can switch to
 * "Create New" mid-flow.
 */
export function ResumeUploadDialog({ open, onOpenChange, onUploaded }: ResumeUploadDialogProps) {
  const handleUpload = async (
    file: File,
    progress: IntakeProgressReporter,
  ): Promise<IntakeHandlerResult> => {
    progress.setStages([
      { key: 'upload', label: 'Uploading file…' },
      { key: 'parse', label: 'Parsing resume content…' },
      { key: 'save', label: 'Saving resume…' },
    ]);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
    const response = await fetch('/api/settings/resumes', {
      method: 'POST',
      body: formData,
    });
    progress.setStage('parse');
    const payload = (await response.json().catch(() => null)) as
      | { success?: boolean; error?: string }
      | null;
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || 'Failed to upload resume');
    }
    progress.setStage('save');
    await onUploaded?.();
    return { shouldClose: true };
  };

  return (
    <ResumeIntakeDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Upload Resume"
      eyebrow="Resume Library"
      description="Upload a PDF, DOCX, or TXT — or switch to Create New for a blank builder resume."
      initialMode="upload"
      hideExistingList
      onUpload={handleUpload}
    />
  );
}
